import React from 'react'
import { useFeatureFlags } from '../../context/featureFlags/FeatureFlagContext'
import { FeatureFlagKeys } from '../../context/featureFlags/FeatureFlagKeys'
import { AddedToBasketConfirmationNew } from './new/AddedToBasketConfirmationNew'
import { AddedToBasketConfirmationOld } from './old/AddedToBasketConfirmationOld'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import { AnalyticsEvent } from '../../services/Analytics'

export interface IBasketItemDetails {
  /** Title of the item */
  title: string
  /** SKU of the item */
  sku: string
  /** Quantity of the item */
  quantity: number
  /** Amount of the item */
  amount: number
  /** Currency code */
  currencyCode: string
  /** Whether the item requires documentation */
  requiresDocumentation: boolean
}

interface IAddedToBasketConfirmationProps {
  /** Basket item details */
  basketItemDetails: IBasketItemDetails
  /** Whether an item was removed */
  wasItemRemoved: boolean
  /** Handle closing the confirmation */
  handleClose?: () => void
  /** Handle go to the basket */
  handleGoToBasket: () => void
  /** Handle go to the checkout - only required for new basket popup */
  handleProceedToCheckout?: () => void
  /** Country code */
  countryCode?: string
}

export const AddedToBasketConfirmation: React.FC<
  IAddedToBasketConfirmationProps
> = ({
  basketItemDetails,
  wasItemRemoved,
  handleClose,
  handleGoToBasket,
  handleProceedToCheckout,
  countryCode
}) => {
  const { useFeatureFlag } = useFeatureFlags()
  const enableNewBasketPopup = useFeatureFlag(FeatureFlagKeys.NewBasketPopup)

  const handleBasketClick = () => {
    analyticsServiceSingleton.trackEvent(AnalyticsEvent.ViewBasket, {
      variant: 'control',
      via_drawer: false
    })
    handleGoToBasket()
  }

  const handleCheckoutClick = () => {
    if (handleProceedToCheckout) {
      analyticsServiceSingleton.trackEvent(AnalyticsEvent.NavigateToCheckout, {
        variant: 'control',
        from_drawer: false
      })
      handleProceedToCheckout()
    }
  }

  // The old component only has a single "View Basket & Checkout" button
  const handleOldBasketClick = () => {
    analyticsServiceSingleton.trackEvent(AnalyticsEvent.ViewBasket, {
      variant: 'control',
      via_drawer: false
    })
    handleGoToBasket()
  }

  return enableNewBasketPopup ? (
    <AddedToBasketConfirmationNew
      basketItemDetails={basketItemDetails}
      wasItemRemoved={wasItemRemoved}
      handleClose={handleClose}
      handleGoToBasket={handleBasketClick}
      handleProceedToCheckout={handleCheckoutClick}
      countryCode={countryCode}
    />
  ) : (
    <AddedToBasketConfirmationOld
      basketItemDetails={basketItemDetails}
      wasItemRemoved={wasItemRemoved}
      handleClose={handleClose}
      handleGoToBasket={handleOldBasketClick}
      countryCode={countryCode}
    />
  )
}
